import React, { Fragment, useRef, useState } from 'react';
import Loading from 'react-loading-components';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { format } from 'date-fns';
import NotificationSystem from 'react-notification-system';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import moment from 'moment';

import { ButtonLink } from 'components/Button';
import Edit from 'components/Icons/Edit';
import Check from 'components/Icons/Check';
import Cancel from 'components/Icons/Cancel';
import Close from 'components/Icons/Close';
import Input from 'components/Form/Input';
import { formatCurrency } from 'utils/formatCurrency';

import style from './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
});

const unmask = val => val.replace(/[$, ]+/g, '');

const SettlementTable = (props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentCreditor, setCurrentCreditor] = useState('');
  const [settlementId, setSettlementId] = useState('');
  const [finalAccountBalance, setFinalAccountBalance] = useState('');
  const [amount, setAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState('');
  const [dscFee, setDscFee] = useState('');
  const [isActual, setIsActual] = useState(false);

  const notification = useRef(null);
  const newActual = useRef(null);
  const newEstimated = useRef(null);

  const handleEditRow = (index, e) => {
    const { rows } = props;
    e.preventDefault();
    setIsEditing(index);
    setCurrentCreditor(rows[index].currentCreditor);
    setSettlementId(rows[index].settlementId);
    setAmount(rows[index].amount);
    setDscFee(rows[index].dscFee);
    setSettlementDate(format(new Date(rows[index].settlementDate), 'MM/dd/yyyy') || '');
    setFinalAccountBalance(rows[index].finalAccountBalance);
    setIsActual(rows[index].isActual);
  };

  const handleSaveRow = (index, e) => {
    e.preventDefault();
    if (
      amount === '' ||
      !unmask(`${finalAccountBalance}`) ||
      (!newActual.current.checked && !newEstimated.current.checked) ||
      !settlementDate ||
      dscFee === '' ||
      !currentCreditor ||
      !settlementId
    ) {
      notification.current
      && notification.current.addNotification({
        message: 'All the fields are required',
        level: 'error',
        position: 'tc',
      });
      return false;
    }

    if (!moment(settlementDate, 'MM/DD/YYYY').isValid()) {
      notification.current
      && notification.current.addNotification({
        message: 'Date is not valid',
        level: 'error',
        position: 'tc',
      });
    } else if (!isActual) {
      notification.current
      && notification.current.addNotification({
        message: 'You can\'t save an estimated creditor. Please select as actual in order to finalize this creditor',
        level: 'error',
        position: 'tc',
      });
    } else {
      props.updateRow(index, {
        currentCreditor,
        settlementId,
        finalAccountBalance: unmask(`${finalAccountBalance}`) || 0,
        amount: unmask(`${amount}`) || 0,
        dscFee: unmask(`${dscFee}`) || 0,
        isActual: newActual.current.checked,
        settlementDate,
      });
      setIsEditing(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setIsEditing(false);
  };

  const handleInputChange = (name, e) => {
    e.preventDefault();
    const { value: targetValue } = e.target;

    switch (name) {
      case 'amount':
        setAmount(targetValue);
        break;
      case 'finalAccountBalance':
        setFinalAccountBalance(targetValue);
        break;
      case 'currentCreditor':
        setCurrentCreditor(targetValue);
        break;
      case 'settlementId':
        setSettlementId(targetValue);
        break;
      case 'settlementDate':
        setSettlementDate(targetValue);
        break;
      case 'dscFee':
        setDscFee(targetValue);
        break;
      default:
        break;
    }
  };

  // eslint-disable-next-line
  const handleRadioChange = (name, e) => {
    switch (name) {
      case 'actual':
        setIsActual(true);
        break;
      default:
        setIsActual(false);
    }
  };

  return (
    <div className={style.table}>
      <NotificationSystem ref={notification} />
      { props.isLoading &&
        <Fragment>
          <div className={style.overlay} />
          <div className={style.loadingIndicator}>
            <Loading type="oval" width={80} height={80} fill="#06bd6c" />
          </div>
        </Fragment> }
      <div className={style.tableWrapper}>
        <div className={style.header}>
          {
            props.columns.map((item, index) => <div className={cn(style[`cell${index + 1}`], style.headerCell)} key={`header-${index}`}>{item.name}</div>)
          }
          <div className={cn(style.cell13, style.headerCell)}>&nbsp;</div>
        </div>
        <div className={style.content}>
          {
            props.rows.map((item, index) => (
              isEditing !== index ?
                <div className={style.row} key={`row-${index}`}>
                  <div className={cn(style.cell1, style.cell)}><strong>{item.creditorName || '-'}</strong></div>
                  <div className={cn(style.cell2, style.cell)}>{item.currentCreditor || '-'}</div>
                  <div className={cn(style.cell3, style.cell)}>{item.settlementId || '-'}
                  </div>
                  <div className={cn(style.cell4, style.cell)}>{item.creditAccountNumber || '-'}</div>
                  <div className={cn(style.cell5, style.cell)}>${formatCurrency(item.accountBalance || 0, 2)}
                  </div>
                  <div className={cn(style.cell6, style.cell)}>${formatCurrency(item.finalAccountBalance || 0, 2)}
                  </div>
                  <div className={cn(style.cell7, style.cell)}>${formatCurrency(item.amount || 0, 2)}
                  </div>
                  <div className={cn(style.cell8, style.cell)}>
                    {
                      !item.isActual ?
                        <Check className={style.editIcon} />
                      :
                        <Cancel className={style.deleteIcon} />
                    }
                  </div>
                  <div className={cn(style.cell9, style.cell)}>
                    {
                      item.isActual ?
                        <Check className={style.editIcon} />
                      :
                        <Cancel className={style.unCheck} />
                    }
                  </div>
                  <div className={cn(style.cell10, style.cell)}>{format(new Date(item.settlementDate), 'MM/dd/yyyy')}</div>
                  <div className={cn(style.cell11, style.cell)}>${(formatCurrency(item.dscFee || 0, 2))}
                  </div>
                  <div className={cn(style.cell12, style.cell)}>
                    ${item.isActual ? formatCurrency(Number(item.lusaFee || 0)) : 0}
                  </div>
                  <div className={cn(style.cell13, style.cell)}>
                    {
                      !(item.isActual && item.isCertificateOfSettlement) &&
                        <ButtonLink onClick={handleEditRow.bind(null, index)}>
                          <Edit className={style.editIcon} />
                        </ButtonLink>
                    }
                  </div>
                </div>
              :
                <div className={style.row} key={`row-${index}`}>
                  <div className={cn(style.cell1, style.cell)}>
                    <Input
                      style={{ border: 'none', textAlign: 'left' }}
                      value={item.creditorName}
                      readOnly
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell2, style.cell)}>
                    <Input
                      value={currentCreditor || ''}
                      noErrorMessage
                      onChange={handleInputChange.bind(null, 'currentCreditor')}
                    />
                  </div>
                  <div className={cn(style.cell3, style.cell)}>
                    <Input
                      value={settlementId || ''}
                      noErrorMessage
                      onChange={handleInputChange.bind(null, 'settlementId')}
                    />
                  </div>
                  <div className={cn(style.cell4, style.cell)}>
                    <Input
                      value={item.creditAccountNumber || '0'}
                      style={{ border: 'none', textAlign: 'left' }}
                      readOnly
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell5, style.cell)}>
                    <Input
                      value={`${item.accountBalance}` || ''}
                      style={{ border: 'none', textAlign: 'left' }}
                      isMasked={currencyMask}
                      readOnly
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell6, style.cell)}>
                    <Input
                      value={`${finalAccountBalance || ''}`}
                      onChange={handleInputChange.bind(null, 'finalAccountBalance')}
                      isMasked={currencyMask}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell7, style.cell)}>
                    <Input
                      value={`${amount || ''}`}
                      onChange={handleInputChange.bind(null, 'amount')}
                      isMasked={currencyMask}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell8, style.cell)}>
                    <input
                      type="radio"
                      name="newEstimated"
                      ref={newEstimated}
                      defaultChecked={!item.isActual}
                      onChange={handleRadioChange.bind(null, 'estimated')}
                    />
                  </div>
                  <div className={cn(style.cell9, style.cell)}>
                    <input
                      type="radio"
                      name="newEstimated"
                      ref={newActual}
                      defaultChecked={item.isActual}
                      onChange={handleRadioChange.bind(null, 'actual')}
                    />
                  </div>
                  <div className={cn(style.cell10, style.cell)}>
                    <Input
                      onChange={handleInputChange.bind(null, 'settlementDate')}
                      value={`${settlementDate}`}
                      placeholder="MM/DD/YYYY"
                      isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell11, style.cell)}>
                    <Input
                      value={`${dscFee || ''}`}
                      isMasked={currencyMask}
                      noErrorMessage
                      onChange={handleInputChange.bind(null, 'dscFee')}
                    />
                  </div>
                  <div className={cn(style.cell12, style.cell)}>
                    <Input
                      value={`${item.isActual ? (Number(item.lusaFee || 0)) : 0}`}
                      isMasked={currencyMask}
                      noErrorMessage
                      readOnly
                    />
                  </div>
                  <div className={cn(style.cell13, style.cell)}>
                    <ButtonLink onClick={handleSaveRow.bind(null, index)}>
                      <Check className={style.editIcon} />
                    </ButtonLink>
                    <ButtonLink onClick={handleCancel}>
                      <Close className={style.deleteIcon} />
                    </ButtonLink>
                  </div>
                </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

SettlementTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  updateRow: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

SettlementTable.defaultProps = {
};

export default SettlementTable;



// WEBPACK FOOTER //
// ./src/components/SettlementTable/index.js